import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  Users, 
  Plus, 
  UserCheck, 
  AlertTriangle, 
  FileText, 
  Clock,
  Calendar,
  User,
  Check,
  X,
  Upload,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface SessionCheckoutDialogProps {
  sessionId: string;
  sessionData?: {
    id: string;
    start_date: string;
    duration_minutes: number;
    classes?: {
      courses?: {
        name: string;
      };
    };
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCheckoutCompleted?: () => void;
}

interface Participant {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  present?: boolean;
}

interface ParticipantData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface CheckoutData {
  content_covered: string;
  all_content_delivered: boolean;
  issues: string;
  observations: string;
}

const SessionCheckoutDialog = ({ 
  sessionId, 
  sessionData, 
  open, 
  onOpenChange, 
  onCheckoutCompleted 
}: SessionCheckoutDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [newParticipantFirstName, setNewParticipantFirstName] = useState('');
  const [newParticipantLastName, setNewParticipantLastName] = useState('');
  const [newParticipantEmail, setNewParticipantEmail] = useState('');
  const [existingCheckout, setExistingCheckout] = useState<any>(null);
  const [previousSessionId, setPreviousSessionId] = useState<string>('');
  
  // Import state
  const [activeTab, setActiveTab] = useState('manual');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState('');
  const [parsedParticipants, setParsedParticipants] = useState<ParticipantData[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    content_covered: '',
    all_content_delivered: true,
    issues: '',
    observations: ''
  });

  useEffect(() => {
    if (open && sessionId) {
      // Check if this is a different session than before
      const isDifferentSession = sessionId !== previousSessionId;
      
      if (isDifferentSession) {
        // Reset all form states when opening dialog for a different session
        setCheckoutData({
          content_covered: '',
          all_content_delivered: true,
          issues: '',
          observations: ''
        });
        setExistingCheckout(null);
        setParticipants([]);
        setNewParticipantFirstName('');
        setNewParticipantLastName('');
        setNewParticipantEmail('');
        setActiveTab('manual');
        setCsvFile(null);
        setTextInput('');
        setParsedParticipants([]);
        setValidationErrors([]);
        setShowPreview(false);
        
        // Update the previous session ID
        setPreviousSessionId(sessionId);
      }
      
      fetchParticipants();
      fetchExistingCheckout();
    } else if (!open) {
      // Reset previous session ID when dialog is closed
      setPreviousSessionId('');
    }
  }, [open, sessionId, previousSessionId]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to search for customer contact by email
  const searchCustomerContact = async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .ilike('email', email);

      if (error) {
        console.error('❌ Erro ao buscar customer_contact:', error);
        throw error;
      }

      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar customer_contact:', error);
      return null;
    }
  };

  // Helper function to search contact in HubSpot
  const searchContactInHubSpot = async (email: string) => {
    console.log('🔍 Buscando contato no HubSpot:', email);
    
    try {
      const { data, error, response } = await supabase.functions.invoke('search-contact-hubspot', {
        body: { email }
      });
      
      console.log('📥 Resposta do HubSpot search:', { data, error, response });
      
      if (error) {
        console.error('❌ Erro ao chamar search-contact-hubspot:', error);
        return null;
      }
      
      if (data?.ok && data?.data?.results?.length > 0) {
        console.log('✅ Contato encontrado no HubSpot:', data.data.results[0]);
        return data.data.results[0];
      } else {
        console.log('ℹ️ Contato não encontrado no HubSpot');
        return null;
      }
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar no HubSpot:', error);
      return null;
    }
  };

  // Helper function to create contact in HubSpot
  const createContactInHubSpot = async (contactData: {
    email: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
  }) => {
    try {
      console.log('➕ Criando contato no HubSpot:', contactData);
      
      const upsertData = {
        contactId: "", // Empty for new contact
        companyId: "", // Could be set if we have company association
        firstname: contactData.firstname || "",
        lastname: contactData.lastname || "",
        email: contactData.email,
        jobtitle: "", // Could be added to form if needed
        mobilephone: contactData.phone || "",
        hs_persona: "" // Could be set based on business logic
      };

      const response = await supabase.functions.invoke('upsert-contact-hubspot', {
        body: upsertData
      });

      console.log('📥 Resposta da criação no HubSpot:', response);

      if (response.error) {
        console.error('❌ Erro ao chamar upsert-contact-hubspot:', response.error);
        throw new Error('Erro ao criar contato no HubSpot');
      }

      const result = response.data;
      if (result?.ok && result?.contactId) {
        console.log('✅ Contato criado no HubSpot com sucesso. Contact ID:', result.contactId);
        return result.contactId; // Return the HubSpot contact ID
      } else {
        console.error('❌ Falha na criação do contato no HubSpot:', result?.error);
        throw new Error(result?.error || 'Erro ao criar contato no HubSpot');
      }
    } catch (error) {
      console.error('❌ Erro inesperado ao criar no HubSpot:', error);
      throw error;
    }
  };

  // Helper function to create customer_contact record
  const createCustomerContact = async (contactData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    job_title?: string;
  }, hubspotId?: string) => {
    console.log('📝 Criando customer_contact:', contactData, 'HubSpot ID:', hubspotId);
    
    // First, get the customer_id from the class subscription via session
    console.log('🔍 Buscando customer_id para a sessão:', sessionId);
    const { data: sessionInfo, error: sessionError } = await supabase
      .from('class_sessions')
      .select(`
        class_id,
        classes!inner(
          subscription_id,
          subscriptions!inner(
            id,
            subscription_customers!inner(
              customer_id
            )
          )
        )
      `)
      .eq('id', sessionId)
      .single();

    if (sessionError) {
      console.error('❌ Erro ao buscar customer_id:', sessionError);
      throw new Error('Erro ao obter customer_id da sessão');
    }

    const customerId = (sessionInfo?.classes as any)?.subscriptions?.subscription_customers?.[0]?.customer_id;
    if (!customerId) {
      console.error('❌ customer_id não encontrado para a sessão');
      throw new Error('customer_id não encontrado para esta sessão');
    }

    console.log('✅ customer_id encontrado:', customerId);
    
    // Insert customer contact
    const { data: customerContact, error: customerContactError } = await supabase
      .from('customer_contacts')
      .insert({
        customer_id: customerId,
        first_name: contactData.first_name,
        last_name: contactData.last_name,
        email: contactData.email,
        phone: contactData.phone || null,
        job_title: contactData.job_title || null,
        is_active: true
      })
      .select()
      .single();

    if (customerContactError) {
      console.error('❌ Erro ao criar customer_contact:', customerContactError);
      throw customerContactError;
    }

    console.log('✅ Customer contact criado:', customerContact);

    // Create external_ids record if we have a HubSpot ID
    if (hubspotId && customerContact) {
      console.log('🔗 Criando external_id para HubSpot:', hubspotId);
      const { error: externalIdError } = await supabase
        .from('external_ids')
        .insert({
          entity_name: 'customer_contacts',
          entity_id: customerContact.id,
          system_name: 'hubspot',
          external_id: hubspotId
        });

      if (externalIdError) {
        console.error('⚠️ Erro ao criar external_id:', externalIdError);
        // Don't throw here, as the main customer contact was created successfully
      } else {
        console.log('✅ External ID criado com sucesso');
      }
    }

    return customerContact;
  };

  // Main function to handle participant with HubSpot integration
  const processParticipantWithHubSpot = async (participantData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  }) => {
    console.log('🎯 Processando participante com integração HubSpot:', participantData);
    
    let customerContactId = null;
    let isCustomerContact = false;

    try {
      // Step 1: Check if email exists in customer_contacts
      console.log('📋 Verificando customer_contacts...');
      const existingCustomerContact = await searchCustomerContact(participantData.email);
      
      if (existingCustomerContact) {
        console.log('✅ Contato encontrado em customer_contacts:', existingCustomerContact);
        customerContactId = existingCustomerContact.id;
        isCustomerContact = true;
      } else {
        console.log('ℹ️ Contato não encontrado em customer_contacts, buscando no HubSpot...');
        
        // Step 2: Search in HubSpot
        const hubspotContact = await searchContactInHubSpot(participantData.email);
        
        if (hubspotContact) {
          console.log('✅ Contato encontrado no HubSpot, criando customer_contact...');
          
          // Create customer_contact from HubSpot data
          const customerContact = await createCustomerContact({
            first_name: hubspotContact.properties.firstname || participantData.first_name,
            last_name: hubspotContact.properties.lastname || participantData.last_name,
            email: participantData.email,
            phone: hubspotContact.properties.mobilephone || participantData.phone,
            job_title: hubspotContact.properties.jobtitle || undefined
          }, hubspotContact.id);
          
          customerContactId = customerContact.id;
          isCustomerContact = true;
        } else {
          console.log('ℹ️ Contato não encontrado no HubSpot, criando novo...');
          
          // Step 3: Create contact in HubSpot first, then in customer_contacts
          try {
            const newHubspotContact = await createContactInHubSpot({
              email: participantData.email,
              firstname: participantData.first_name,
              lastname: participantData.last_name,
              phone: participantData.phone
            });
            
            console.log('✅ Contato criado no HubSpot, criando customer_contact...');
            
            // Create customer_contact with HubSpot ID
            const customerContact = await createCustomerContact({
              first_name: participantData.first_name,
              last_name: participantData.last_name,
              email: participantData.email,
              phone: participantData.phone
            }, newHubspotContact);
            
            customerContactId = customerContact.id;
            isCustomerContact = true;
            
            console.log('🎉 Fluxo completo HubSpot concluído com sucesso!');
          } catch (hubspotError) {
            console.error('❌ Erro na criação no HubSpot:', hubspotError);
            
            // Fallback: Create customer_contact without HubSpot sync
            console.log('🔄 Criando customer_contact sem sincronização HubSpot...');
            try {
              const customerContact = await createCustomerContact({
                first_name: participantData.first_name,
                last_name: participantData.last_name,
                email: participantData.email,
                phone: participantData.phone
              });
              
              customerContactId = customerContact.id;
              isCustomerContact = false; // Not synced with HubSpot
              
              console.log('✅ Customer contact criado sem HubSpot sync');
            } catch (fallbackError) {
              console.error('❌ Erro no fallback:', fallbackError);
              // Continue without customer_contact
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro no processamento com HubSpot:', error);
      // Continue without customer_contact integration
    }

    console.log('📊 Resultado do processamento:', { customer_contact_id: customerContactId, is_customer_contact: isCustomerContact });
    
    return {
      customer_contact_id: customerContactId,
      is_customer_contact: isCustomerContact
    };
  };

  const validateParticipant = (participant: ParticipantData, index: number): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (!participant.first_name || participant.first_name.trim().length < 2) {
      errors.push({
        row: index + 1,
        field: 'first_name',
        message: 'Nome deve ter pelo menos 2 caracteres'
      });
    }

    if (!participant.last_name || participant.last_name.trim().length < 2) {
      errors.push({
        row: index + 1,
        field: 'last_name',
        message: 'Sobrenome deve ter pelo menos 2 caracteres'
      });
    }

    if (!participant.email || !validateEmail(participant.email)) {
      errors.push({
        row: index + 1,
        field: 'email',
        message: 'Email inválido'
      });
    }

    return errors;
  };

  const parseCSV = (content: string): ParticipantData[] => {
    const lines = content.split('\n').filter(line => line.trim());
    const participants: ParticipantData[] = [];
    
    // Skip header if present
    const startIndex = lines[0]?.toLowerCase().includes('nome') || lines[0]?.toLowerCase().includes('email') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const values = lines[i].split(',').map(val => val.trim().replace(/"/g, ''));
      if (values.length >= 3) { // Now expecting first_name, last_name, email, phone
        participants.push({
          first_name: values[0] || '',
          last_name: values[1] || '',
          email: values[2] || '',
          phone: values[3] || ''
        });
      }
    }
    
    return participants;
  };

  const parseTextInput = (text: string): ParticipantData[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const participants: ParticipantData[] = [];
    
    for (const line of lines) {
      const values = line.split(',').map(val => val.trim());
      if (values.length >= 3) { // Now expecting first_name, last_name, email, phone
        participants.push({
          first_name: values[0] || '',
          last_name: values[1] || '',
          email: values[2] || '',
          phone: values[3] || ''
        });
      }
    }
    
    return participants;
  };

  const resetImportForm = () => {
    setCsvFile(null);
    setTextInput('');
    setParsedParticipants([]);
    setValidationErrors([]);
    setShowPreview(false);
    setActiveTab('manual');
  };

  const fetchParticipants = async () => {
    setLoading(true);
    try {
      // Get class_id from session
      const { data: sessionInfo, error: sessionError } = await supabase
        .from('class_sessions')
        .select('class_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Get participants for this class
      const { data, error } = await supabase
        .from('class_participants')
        .select('id, first_name, last_name, email')
        .eq('class_id', sessionInfo.class_id);

      if (error) throw error;

      // Get existing attendance records
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('class_attendance')
        .select('participant_id, is_present')
        .eq('session_id', sessionId);

      if (attendanceError && attendanceError.code !== 'PGRST116') {
        console.error('Error fetching attendance:', attendanceError);
      }

      // Merge participant data with attendance
      const participantsWithAttendance = data?.map(participant => ({
        ...participant,
        present: attendanceData?.find(a => a.participant_id === participant.id)?.is_present || false
      })) || [];

      setParticipants(participantsWithAttendance);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Erro ao carregar participantes');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingCheckout = async () => {
    try {
      console.log('DEBUG - SessionCheckoutDialog fetching for session ID:', sessionId);
      
      const { data, error } = await supabase
        .from('session_checkouts')
        .select('*')
        .eq('session_id', sessionId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // Handle RLS or table not found errors gracefully
        if (error.code === '42P01' || error.code === '406') {
          console.warn('session_checkouts table may not exist or have RLS issues:', error);
          return;
        }
        throw error;
      }

      if (data) {
        setExistingCheckout(data);
        setCheckoutData({
          content_covered: data.content_covered || '',
          all_content_delivered: data.all_content_delivered || true,
          issues: data.issues || '',
          observations: data.observations || ''
        });
      }
    } catch (error) {
      console.error('Error fetching existing checkout:', error);
    }
  };

  const toggleAttendance = async (participantId: string) => {
    const participant = participants.find(p => p.id === participantId);
    if (!participant) return;

    const newAttendanceStatus = !participant.present;

    try {
      // First, check if attendance record already exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('class_attendance')
        .select('id')
        .eq('session_id', sessionId)
        .eq('participant_id', participantId)
        .maybeSingle();

      if (checkError) throw checkError;

      let result;
      if (existingRecord) {
        // Update existing record
        result = await supabase
          .from('class_attendance')
          .update({
            is_present: newAttendanceStatus,
            check_in_time: newAttendanceStatus ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('session_id', sessionId)
          .eq('participant_id', participantId);
      } else {
        // Insert new record
        result = await supabase
          .from('class_attendance')
          .insert({
            session_id: sessionId,
            participant_id: participantId,
            is_present: newAttendanceStatus,
            check_in_time: newAttendanceStatus ? new Date().toISOString() : null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
      }

      if (result.error) throw result.error;

      // Update local state
      setParticipants(prev => 
        prev.map(p => 
          p.id === participantId 
            ? { ...p, present: newAttendanceStatus }
            : p
        )
      );

      const participantName = `${participant.first_name} ${participant.last_name}`;
      toast.success(`Presença ${newAttendanceStatus ? 'marcada' : 'desmarcada'} para ${participantName}`);
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Erro ao atualizar presença');
    }
  };

  const addNewParticipant = async () => {
    if (!newParticipantFirstName.trim() || !newParticipantEmail.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    setLoading(true);
    console.log('🚀 Iniciando processamento com integração HubSpot...');

    try {
      // Get class_id from session
      const { data: sessionInfo, error: sessionError } = await supabase
        .from('class_sessions')
        .select('class_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Check if participant already exists in this class
      const { data: existing } = await supabase
        .from('class_participants')
        .select('*')
        .eq('class_id', sessionInfo.class_id)
        .ilike('email', newParticipantEmail);

      if (existing && existing.length > 0) {
        toast.error('Participante já cadastrado nesta formação');
        return;
      }

      const participantData = {
        first_name: newParticipantFirstName.trim(),
        last_name: newParticipantLastName.trim(),
        email: newParticipantEmail,
        phone: ''
      };

      // Process participant with HubSpot integration
      const { customer_contact_id, is_customer_contact } = await processParticipantWithHubSpot(participantData);

      console.log('👥 Criando participante na tabela class_participants...');

      // Add participant to class_participants
      const { data, error } = await supabase
        .from('class_participants')
        .insert({
          class_id: sessionInfo.class_id,
          customer_contact_id: customer_contact_id,
          first_name: newParticipantFirstName.trim(),
          last_name: newParticipantLastName.trim(),
          email: newParticipantEmail,
          phone: null,
          is_customer_contact: is_customer_contact,
          status: 'registered',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      console.log('🎉 Participante criado com sucesso!');

      // Add participant to attendance with present=true by default
      const { error: attendanceError } = await supabase
        .from('class_attendance')
        .insert({
          session_id: sessionId,
          participant_id: data.id,
          is_present: true, // Mark as present by default
          check_in_time: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (attendanceError) {
        console.error('Error creating attendance record:', attendanceError);
      }

      // Add to participants list
      setParticipants(prev => [...prev, {
        ...data,
        present: true // Mark as present in the UI
      }]);

      setNewParticipantFirstName('');
      setNewParticipantLastName('');
      setNewParticipantEmail('');

      const successMessage = is_customer_contact
        ? 'Participante adicionado e vinculado ao HubSpot com sucesso!'
        : 'Participante adicionado com sucesso!';
      toast.success(successMessage);
    } catch (error) {
      console.error('Error adding participant:', error);
      toast.error('Erro ao adicionar participante');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV');
      return;
    }

    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const participants = parseCSV(content);
      setParsedParticipants(participants);
      
      // Validate all participants
      const allErrors: ValidationError[] = [];
      participants.forEach((participant, index) => {
        const errors = validateParticipant(participant, index);
        allErrors.push(...errors);
      });
      
      setValidationErrors(allErrors);
      setShowPreview(true);
    };
    
    reader.readAsText(file);
  };

  const handleTextImport = () => {
    if (!textInput.trim()) {
      toast.error('Por favor, cole os dados dos participantes');
      return;
    }

    const participants = parseTextInput(textInput);
    setParsedParticipants(participants);
    
    // Validate all participants
    const allErrors: ValidationError[] = [];
    participants.forEach((participant, index) => {
      const errors = validateParticipant(participant, index);
      allErrors.push(...errors);
    });
    
    setValidationErrors(allErrors);
    setShowPreview(true);
  };

  const handleBulkSubmit = async () => {
    if (validationErrors.length > 0) {
      toast.error('Por favor, corrija os erros antes de prosseguir');
      return;
    }

    setSaving(true);
    console.log('🚀 Iniciando importação em lote com integração HubSpot...');
    
    try {
      // Get class_id from session
      const { data: sessionInfo, error: sessionError } = await supabase
        .from('class_sessions')
        .select('class_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Check for existing participants
      const emails = parsedParticipants.map(p => p.email);
      const { data: existing } = await supabase
        .from('class_participants')
        .select('email')
        .eq('class_id', sessionInfo.class_id)
        .in('email', emails);

      const existingEmails = existing?.map(e => e.email) || [];
      const newParticipants = parsedParticipants.filter(p => !existingEmails.includes(p.email));

      if (existingEmails.length > 0) {
        toast.warning(`${existingEmails.length} participante(s) já cadastrado(s) foram ignorado(s)`);
      }

      if (newParticipants.length === 0) {
        toast.error('Todos os participantes já estão cadastrados');
        return;
      }

      // Process each participant with HubSpot integration
      const participantsToInsert = [];
      let hubspotSyncedCount = 0;

      for (const participant of newParticipants) {
        try {
          const participantData = {
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone
          };

          const { customer_contact_id, is_customer_contact } = await processParticipantWithHubSpot(participantData);

          if (is_customer_contact) {
            hubspotSyncedCount++;
          }

          participantsToInsert.push({
            class_id: sessionInfo.class_id,
            customer_contact_id: customer_contact_id,
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone || null,
            is_customer_contact: is_customer_contact,
            status: 'registered',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        } catch (error) {
          console.error(`Error processing participant ${participant.email}:`, error);
          // Continue with participant without HubSpot sync
          participantsToInsert.push({
            class_id: sessionInfo.class_id,
            customer_contact_id: null,
            first_name: participant.first_name.trim(),
            last_name: participant.last_name.trim(),
            email: participant.email,
            phone: participant.phone || null,
            is_customer_contact: false,
            status: 'registered',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }
      }

      const { data, error } = await supabase
        .from('class_participants')
        .insert(participantsToInsert)
        .select();

      if (error) throw error;

      // Add to participants list
      const newParticipantsWithPresence = data?.map(p => ({ ...p, present: false })) || [];
      setParticipants(prev => [...prev, ...newParticipantsWithPresence]);

      const baseMessage = `${newParticipants.length} participante(s) cadastrado(s) com sucesso!`;
      const hubspotMessage = hubspotSyncedCount > 0 
        ? ` ${hubspotSyncedCount} sincronizado(s) com HubSpot.`
        : '';
      toast.success(baseMessage + hubspotMessage);
      
      resetImportForm();
    } catch (error) {
      console.error('Error adding bulk participants:', error);
      toast.error('Erro ao cadastrar participantes');
    } finally {
      setSaving(false);
    }
  };

  const removeParticipant = (index: number) => {
    const updated = parsedParticipants.filter((_, i) => i !== index);
    setParsedParticipants(updated);
    
    // Re-validate
    const allErrors: ValidationError[] = [];
    updated.forEach((participant, index) => {
      const errors = validateParticipant(participant, index);
      allErrors.push(...errors);
    });
    setValidationErrors(allErrors);
  };

  const generateCertificateNumber = (participantId: string, classId: string): string => {
    const timestamp = Date.now().toString().slice(-6);
    const participantHash = participantId.slice(-4);
    const classHash = classId.slice(-4);
    return `CERT-${classHash}-${participantHash}-${timestamp}`;
  };

  const ensureUniqueCertificateNumber = async (baseNumber: string): Promise<string> => {
    let uniqueNumber = baseNumber;
    let counter = 1;
    
    while (true) {
      const { data, error } = await supabase
        .from('class_certificates')
        .select('id')
        .eq('certificate_number', uniqueNumber)
        .limit(1);
        
      if (error) throw error;
      
      if (!data || data.length === 0) {
        return uniqueNumber;
      }
      
      uniqueNumber = `${baseNumber}-${counter}`;
      counter++;
    }
  };

  const checkAndGenerateCertificates = async () => {
    try {
      // Get class_id from session
      const { data: sessionInfo, error: sessionError } = await supabase
        .from('class_sessions')
        .select('class_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Get all sessions for this class
      const { data: allSessions, error: sessionsError } = await supabase
        .from('class_sessions')
        .select('id')
        .eq('class_id', sessionInfo.class_id)
        .in('status', ['done', 'confirmed']); // Check both 'done' and 'confirmed' sessions

      if (sessionsError) throw sessionsError;

      if (!allSessions || allSessions.length === 0) {
        console.log('No confirmed sessions found for this class');
        return;
      }

      // Check if all sessions have done checkouts - can have either 'done' or 'completed' status for backwards compatibility
      const { data: checkouts, error: checkoutsError } = await supabase
        .from('session_checkouts')
        .select('session_id')
        .in('session_id', allSessions.map(s => s.id))
        .in('status', ['done', 'completed']);

      if (checkoutsError) {
        console.error('Error checking session checkouts:', checkoutsError);
        return; // Don't throw, just log and continue
      }

      const doneCheckoutSessionIds = new Set(checkouts?.map(c => c.session_id) || []);
      const allSessionsDone = allSessions.every(session => 
        doneCheckoutSessionIds.has(session.id)
      );

      console.log('Sessions status:', {
        totalSessions: allSessions.length,
        doneCheckouts: doneCheckoutSessionIds.size,
        allDone: allSessionsDone
      });

      if (!allSessionsDone) {
        console.log('Not all sessions have done checkouts yet');
        return;
      }

      // All sessions have checkout done, generate certificates
      console.log('All sessions done, generating certificates...');
      
      // Get all participants (attendance requirement removed)
      const { data: participants, error: participantsError } = await supabase
        .from('class_participants')
        .select('id, first_name, last_name, email')
        .eq('class_id', sessionInfo.class_id);

      if (participantsError) throw participantsError;

      if (!participants || participants.length === 0) {
        console.log('No participants found for this class');
        return;
      }

      // Get attendance data for all sessions
      const { data: attendances, error: attendanceError } = await supabase
        .from('class_attendance')
        .select('participant_id, is_present')
        .in('session_id', allSessions.map(s => s.id));

      if (attendanceError) throw attendanceError;

      // Check existing certificates
      const { data: existingCertificates, error: existingCertError } = await supabase
        .from('class_certificates')
        .select('participant_id')
        .eq('class_id', sessionInfo.class_id);

      if (existingCertError) throw existingCertError;

      const existingCertificateParticipantIds = new Set(
        existingCertificates?.map(c => c.participant_id) || []
      );

      // Calculate eligibility and prepare certificates
      const certificatesToInsert = [];
      let eligibleCount = 0;
      let alreadyHasCertificateCount = 0;

      for (const participant of participants) {
        // Check if already has certificate
        if (existingCertificateParticipantIds.has(participant.id)) {
          alreadyHasCertificateCount++;
          continue;
        }

        // Calculate attendance rate
        const participantAttendances = attendances?.filter(
          att => att.participant_id === participant.id
        ) || [];
        
        const presentCount = participantAttendances.filter(att => att.is_present).length;
        const attendanceRate = (presentCount / allSessions.length) * 100;

        // Removed 75% attendance requirement - all participants are eligible
        eligibleCount++;
        const initialNumber = generateCertificateNumber(participant.id, sessionInfo.class_id);
        const uniqueNumber = await ensureUniqueCertificateNumber(initialNumber);
        
        certificatesToInsert.push({
          class_id: sessionInfo.class_id,
          participant_id: participant.id,
          certificate_number: uniqueNumber,
          issued_date: new Date().toISOString(),
          status: 'issued'
        });
      }

      console.log('Certificate generation summary:', {
        totalParticipants: participants.length,
        eligibleForCertificate: eligibleCount,
        alreadyHasCertificate: alreadyHasCertificateCount,
        newCertificatesToGenerate: certificatesToInsert.length
      });

      if (certificatesToInsert.length > 0) {
        // Insert certificates
        const { error: insertError } = await supabase
          .from('class_certificates')
          .insert(certificatesToInsert);

        if (insertError) throw insertError;
        
        // Update class status to finished after certificates are generated
        console.log('Updating class status to finished after certificate generation');
        const { error: updateClassError } = await supabase
          .from('classes')
          .update({ 
            status: 'finished',
            updated_at: new Date().toISOString()
          })
          .eq('id', sessionInfo.class_id);
          
        if (updateClassError) {
          console.error('Error updating class status:', updateClassError);
        } else {
          console.log('Class status updated to finished successfully');
        }
        
        // Wait a short time to ensure certificates are committed to the database
        await new Promise(resolve => setTimeout(resolve, 300));

        // Log important data for debugging
        console.log('Certificate generation complete. Details:', {
          classId: sessionInfo.class_id,
          certificatesGenerated: certificatesToInsert.length,
          certificateIds: certificatesToInsert.map(c => c.certificate_number)
        });

        toast.success(
          `🎓 Todos os check-outs concluídos! ${certificatesToInsert.length} certificado(s) gerado(s) automaticamente.`,
          { duration: 5000 }
        );
      } else if (alreadyHasCertificateCount > 0) {
        // If certificates already exist, still make sure the class is marked as finished
        console.log('Certificates already exist, ensuring class status is finished');
        const { error: updateClassError } = await supabase
          .from('classes')
          .update({ 
            status: 'finished',
            updated_at: new Date().toISOString()
          })
          .eq('id', sessionInfo.class_id);
          
        if (updateClassError) {
          console.error('Error updating class status:', updateClassError);
        } else {
          console.log('Class status updated to finished successfully');
        }
        
        toast.info(
          `✅ Todos os check-outs concluídos! Todos os participantes elegíveis já possuem certificados.`,
          { duration: 4000 }
        );
      } else {
        toast.info(
          `✅ Todos os check-outs concluídos! Nenhum participante elegível para certificado ou todos já possuem certificado.`,
          { duration: 4000 }
        );
      }
    } catch (error) {
      console.error('Error in automatic certificate generation:', error);
      toast.error('Erro ao gerar certificados automaticamente');
    }
  };

  const handleCheckoutSubmit = async () => {
    if (!checkoutData.content_covered.trim()) {
      toast.error('Por favor, descreva o conteúdo aplicado');
      return;
    }

    setSaving(true);
    try {
      const checkoutPayload = {
        session_id: sessionId,
        content_covered: checkoutData.content_covered,
        all_content_delivered: checkoutData.all_content_delivered,
        issues: checkoutData.issues || null,
        observations: checkoutData.observations || null,
        status: 'done',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      let result;
      if (existingCheckout) {
        // Update existing checkout
        result = await supabase
          .from('session_checkouts')
          .update({
            ...checkoutPayload,
            created_at: existingCheckout.created_at
          })
          .eq('id', existingCheckout.id);
      } else {
        // Create new checkout
        result = await supabase
          .from('session_checkouts')
          .insert(checkoutPayload);
      }

      if (result.error) {
        // Handle RLS or table not found errors gracefully
        if (result.error.code === '42P01' || result.error.code === '406') {
          console.warn('session_checkouts table may not exist or have RLS issues:', result.error);
          toast.warning('Funcionalidade de check-out ainda não está configurada no sistema');
          return;
        }
        throw result.error;
      }

      toast.success('Check-out realizado com sucesso!');
      
      // Check if all sessions are now done and generate certificates if needed
      await checkAndGenerateCertificates();
      
      onOpenChange(false);
      onCheckoutCompleted?.();
    } catch (error) {
      console.error('Error completing checkout:', error);
      toast.error('Erro ao realizar check-out');
    } finally {
      setSaving(false);
    }
  };

  const presentCount = participants.filter(p => p.present).length;
  const totalCount = participants.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Check-out da Sessão
          </DialogTitle>
          <DialogDescription>
            {sessionData && (
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  {new Date(sessionData.start_date).toLocaleDateString('pt-BR')}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4" />
                  {sessionData.duration_minutes} minutos
                </div>
                {sessionData.classes?.courses?.name && (
                  <div className="text-sm font-extrabold">
                    {sessionData.classes.courses.name}
                  </div>
                )}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Attendance Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Lista de Presença
                </div>
                <Badge variant="secondary">
                  {presentCount}/{totalCount} presentes
                </Badge>
              </CardTitle>
              <CardDescription>
                Marque a presença dos participantes e adicione novos se necessário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add New Participant */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="manual" className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Manual
                    </TabsTrigger>
                    <TabsTrigger value="csv" className="flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      CSV
                    </TabsTrigger>
                    <TabsTrigger value="text" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Texto
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="manual" className="space-y-3 mt-4">
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        placeholder="Nome"
                        value={newParticipantFirstName}
                        onChange={(e) => setNewParticipantFirstName(e.target.value)}
                      />
                      <Input
                        placeholder="Sobrenome"
                        value={newParticipantLastName}
                        onChange={(e) => setNewParticipantLastName(e.target.value)}
                      />
                      <Input
                        placeholder="Email"
                        type="email"
                        value={newParticipantEmail}
                        onChange={(e) => setNewParticipantEmail(e.target.value)}
                      />
                    </div>
                    <Button 
                      size="sm"
                      onClick={addNewParticipant}
                      disabled={!newParticipantFirstName.trim() || !newParticipantEmail.trim() || loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar
                        </>
                      )}
                    </Button>
                  </TabsContent>

                  <TabsContent value="csv" className="space-y-3 mt-4">
                    <div>
                      <Input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Formato esperado: Nome, Sobrenome, Email, Telefone
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="text" className="space-y-3 mt-4">
                    <div>
                      <Textarea
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="João,Silva,joao@empresa.com,(11) 99999-9999"
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Uma linha por participante. Formato: Nome, Sobrenome, Email, Telefone
                      </p>
                    </div>
                    <Button 
                      size="sm"
                      onClick={handleTextImport} 
                      disabled={!textInput.trim()}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Processar
                    </Button>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Preview Section */}
              {showPreview && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-extrabold flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Preview - {parsedParticipants.length} participante(s)
                    </h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={resetImportForm}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>

                  {validationErrors.length > 0 && (
                    <Alert className="mb-3">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{validationErrors.length} erro(s):</strong>
                        <ul className="mt-1 list-disc list-inside text-xs">
                          {validationErrors.slice(0, 3).map((error, index) => (
                            <li key={index}>
                              Linha {error.row}: {error.message}
                            </li>
                          ))}
                          {validationErrors.length > 3 && (
                            <li>+ {validationErrors.length - 3} outros erros...</li>
                          )}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <ScrollArea className="h-32">
                    <div className="space-y-1">
                      {parsedParticipants.map((participant, index) => {
                        const hasError = validationErrors.some(e => e.row === index + 1);
                        return (
                          <div key={index} className={`flex items-center justify-between p-2 rounded text-sm ${hasError ? 'border border-red-200 bg-red-50' : 'bg-white border'}`}>
                            <div className="flex-1">
                              <div className="font-extrabold">{participant.first_name} {participant.last_name}</div>
                              <div className="text-xs text-gray-500">{participant.email}</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeParticipant(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>

                  <Button 
                    size="sm"
                    className="mt-3 w-full"
                    onClick={handleBulkSubmit}
                    disabled={saving || validationErrors.length > 0}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      `Cadastrar ${parsedParticipants.length} Participante(s)`
                    )}
                  </Button>
                </div>
              )}

              {/* Participants List */}
              <ScrollArea className="h-64">
                <div className="space-y-2">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">
                      Carregando participantes...
                    </div>
                  ) : participants.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Nenhum participante cadastrado
                    </div>
                  ) : (
                    participants.map((participant) => (
                      <div
                        key={participant.id}
                        className="flex items-center justify-between p-2 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-sm">{participant.first_name} {participant.last_name}</span>
                            <span className="text-xs text-gray-500 truncate">• {participant.email}</span>
                          </div>
                        </div>
                        <Button
                          variant={participant.present ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleAttendance(participant.id)}
                          className={`ml-2 ${participant.present ? "bg-green-600 hover:bg-green-700" : ""}`}
                        >
                          {participant.present ? (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Presente
                            </>
                          ) : (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Ausente
                            </>
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Content Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Resumo do Treinamento
              </CardTitle>
              <CardDescription>
                Descreva o conteúdo que foi aplicado na sessão
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="content-covered">Conteúdo Aplicado *</Label>
                <Textarea
                  id="content-covered"
                  value={checkoutData.content_covered}
                  onChange={(e) => setCheckoutData(prev => ({ ...prev, content_covered: e.target.value }))}
                  placeholder="Descreva os tópicos, exercícios e atividades realizadas durante a sessão..."
                  rows={4}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all-content-delivered"
                  checked={checkoutData.all_content_delivered}
                  onCheckedChange={(checked) => 
                    setCheckoutData(prev => ({ ...prev, all_content_delivered: !!checked }))
                  }
                />
                <Label htmlFor="all-content-delivered" className="text-sm">
                  Todo o conteúdo previsto foi ministrado
                </Label>
              </div>

              {!checkoutData.all_content_delivered && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Como nem todo o conteúdo foi ministrado, considere registrar os motivos na seção de observações ou problemas.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Issues Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Registro de Problemas
              </CardTitle>
              <CardDescription>
                Anote ocorrências, imprevistos ou dificuldades enfrentadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={checkoutData.issues}
                onChange={(e) => setCheckoutData(prev => ({ ...prev, issues: e.target.value }))}
                placeholder="Descreva problemas técnicos, dificuldades dos participantes, imprevistos com horário, etc..."
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Observations Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Observações Gerais
              </CardTitle>
              <CardDescription>
                Comentários adicionais relevantes para o histórico
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={checkoutData.observations}
                onChange={(e) => setCheckoutData(prev => ({ ...prev, observations: e.target.value }))}
                placeholder="Comentários sobre engajamento dos participantes, sugestões de melhoria, feedback recebido, etc..."
                rows={3}
              />
            </CardContent>
          </Card>
        </div>

        <Separator />

        <DialogFooter className="flex justify-between">
          <div className="text-sm text-gray-500">
            {existingCheckout ? 'Atualizando check-out existente' : 'Criando novo check-out'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCheckoutSubmit} 
              disabled={saving || !checkoutData.content_covered.trim()}
            >
              {saving ? 'Salvando...' : existingCheckout ? 'Atualizar Check-out' : 'Finalizar Check-out'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionCheckoutDialog;
